/* eslint-env es2020 */
import { ethers } from 'ethers';
import { ERC20ABI } from '../ABI/ABI';
import { readContractData } from './contractInstance';

const STABLE_SYMBOLS = new Set(['USDC', 'EURC']);

export const getAnalyticsStableTokens = (tokenList = []) =>
    tokenList.filter((token) =>
        STABLE_SYMBOLS.has(String(token.symbol || '').toUpperCase()),
    );

const readOptional = async (readContract, address, abi, names, args = []) => {
    const candidates = Array.isArray(names) ? names : [names];
    for (const functionName of candidates) {
        try {
            return await readContract(address, abi, functionName, args);
        } catch (_error) {
            // try next candidate
        }
    }
    return null;
};

const fetchStableTokenBalances = async ({
    holderAddress,
    stableTokens,
    readContract,
}) => {
    if (!holderAddress || !stableTokens.length) return [];

    const rows = await Promise.all(
        stableTokens.map(async (token) => {
            try {
                const rawBalance = await readContract(
                    token.address,
                    ERC20ABI,
                    'balanceOf',
                    [holderAddress],
                );
                const balanceFloat = parseFloat(
                    ethers.formatUnits(rawBalance, token.decimals ?? 6),
                );
                return {
                    address: token.address,
                    symbol: token.symbol,
                    name: token.name,
                    decimals: token.decimals ?? 6,
                    logoURI: token.logoURI,
                    balanceRaw: rawBalance?.toString?.() ?? String(rawBalance),
                    balanceFloat: Number.isFinite(balanceFloat) ? balanceFloat : 0,
                };
            } catch (_error) {
                return {
                    address: token.address,
                    symbol: token.symbol,
                    name: token.name,
                    decimals: token.decimals ?? 6,
                    logoURI: token.logoURI,
                    balanceRaw: '0',
                    balanceFloat: 0,
                };
            }
        }),
    );

    return rows;
};

export const fetchGatewayContractAnalytics = async ({
    gatewayAddress,
    gatewayAbi,
    stableTokens = [],
    readContract = readContractData,
}) => {
    if (!gatewayAddress) {
        return null;
    }

    const [admin, disputeResolutionPeriod, disputeCount, tokenBalances] =
        await Promise.all([
            readOptional(readContract, gatewayAddress, gatewayAbi, [
                'disputeAdmin',
                'admin',
                'owner',
            ]),
            readOptional(readContract, gatewayAddress, gatewayAbi, [
                'disputeResolutionPeriod',
                'getDisputeResolutionPeriod',
            ]),
            readOptional(readContract, gatewayAddress, gatewayAbi, 'disputeCount'),
            fetchStableTokenBalances({
                holderAddress: gatewayAddress,
                stableTokens,
                readContract,
            }),
        ]);

    return {
        address: gatewayAddress,
        admin,
        disputeResolutionPeriodSeconds: Number(disputeResolutionPeriod || 0),
        tokenBalances,
        totalTransactions: Number(disputeCount || 0),
        transactionLabel: 'Disputes recorded on gateway',
    };
};

export const fetchJobEscrowContractAnalytics = async ({
    escrowAddress,
    escrowAbi,
    stableTokens = [],
    readContract = readContractData,
}) => {
    if (!escrowAddress) {
        return { configured: false };
    }

    const [escrowCount, paymentToken, owner, disputeAdmin, paused, tokenBalances] =
        await Promise.all([
            readOptional(readContract, escrowAddress, escrowAbi, 'escrowCount'),
            readOptional(readContract, escrowAddress, escrowAbi, [
                'getWhitelistedTokens',
                'paymentToken',
            ]).then(async (value) => {
                if (Array.isArray(value) && value.length > 0) {
                    return value[0];
                }
                return value;
            }),
            readOptional(readContract, escrowAddress, escrowAbi, 'owner'),
            readOptional(readContract, escrowAddress, escrowAbi, 'disputeAdmin'),
            readOptional(readContract, escrowAddress, escrowAbi, 'paused'),
            fetchStableTokenBalances({
                holderAddress: escrowAddress,
                stableTokens,
                readContract,
            }),
        ]);

    return {
        configured: true,
        address: escrowAddress,
        owner,
        disputeAdmin,
        paused: Boolean(paused),
        paymentToken,
        escrowCount: Number(escrowCount || 0),
        totalTransactions: Number(escrowCount || 0),
        transactionLabel: 'Total escrows created',
        tokenBalances,
    };
};

export const fetchAdminContractAnalytics = async ({
    gatewayAddress,
    gatewayAbi,
    escrowAddress,
    escrowAbi,
    tokenList = [],
    readContract = readContractData,
}) => {
    const stableTokens = getAnalyticsStableTokens(tokenList);
    const errors = [];

    let gateway = null;
    let jobEscrow = { configured: Boolean(escrowAddress) };

    try {
        gateway = await fetchGatewayContractAnalytics({
            gatewayAddress,
            gatewayAbi,
            stableTokens,
            readContract,
        });
    } catch (error) {
        errors.push(
            `Payment gateway: ${error?.shortMessage || error?.message || 'load failed'}`,
        );
    }

    if (escrowAddress) {
        try {
            jobEscrow = await fetchJobEscrowContractAnalytics({
                escrowAddress,
                escrowAbi,
                stableTokens,
                readContract,
            });
        } catch (error) {
            errors.push(
                `Job escrow: ${error?.shortMessage || error?.message || 'load failed'}`,
            );
            jobEscrow = { configured: true, address: escrowAddress, error: true };
        }
    } else {
        jobEscrow = { configured: false };
    }

    return {
        gateway,
        jobEscrow,
        stableTokens: stableTokens.map((t) => t.symbol),
        errors,
        fetchedAt: Date.now(),
    };
};
